import React, { FC, useEffect, useRef, useState } from "react";
// import { useCellContext } from "../components/CellContext";
// import CellWrapper from "../components/CellWrapper";
// import { useDoubleTouch } from "../hooks/useDoubleTouch";
import { useCellContext, CellWrapper, useDoubleTouch, useReactGridAPI, TextCell } from '@silevis/reactgrid'
import CustomContextMenu from "./ContextMenu";
import { shortcutType } from "./reactGridHelper";
import { useHotkeys } from 'react-hotkeys-hook';
import commonStyles from './commonGrid.module.scss';

interface NumberCellProps {
    value: number;
    onValueChanged: (newValue: number) => void;
    validator?: (value: number) => boolean;
    errorMessage?: string;
    hideZero?: boolean;
    allowSeparators?: boolean;
    format?: Intl.NumberFormat;
    style?: React.CSSProperties;
    onAddRow?: (rowIndex: number) => void;
    onDeleteRow?: (rowIndex: number) => void;
    allowShortcuts?: shortcutType[];
}

export const CustomNumberCell: FC<NumberCellProps> = ({
    value: initialValue,
    style,
    onValueChanged,
    validator,
    errorMessage,
    hideZero = true,
    allowSeparators = true,
    format,
    onAddRow,
    onDeleteRow,
    allowShortcuts = []
}) => {
    const initialValueStr = initialValue !== null && initialValue !== undefined
        ? initialValue.toString()
        : "";
    const ctx = useCellContext();

    const targetInputRef = useRef<HTMLInputElement>(null);
    const [isEditMode, setEditMode] = useState(false);
    // const [currentValue, setCurrentValue] = useState(initialValueStr || "0");
    const [currentValue, setCurrentValue] = useState(initialValue !== 0 ? initialValue?.toString() : "");


    const escapePressedRef = useRef(false);
    const { handleDoubleTouch } = useDoubleTouch(ctx, setEditMode);

    const isValid = validator ? validator(Number(initialValue)) : true;

    // display the formatted value or error message
    const getFormattedValue = (): string => {
        // console.log('initialValue', initialValue);
        if (
            initialValue === null ||
            initialValue === undefined ||
            initialValueStr === ""
        )
            return "";
        if (Number.isNaN(Number(initialValue)) || (hideZero && initialValue === 0)) return "";
        if (format) return format.format(initialValue);
        if (!isValid && errorMessage) return errorMessage;

        // show the value as a string without any formatting
        return initialValueStr;
    };

    // Add keyboard shortcuts for adding and deleting rows
    // useHotkeys("ctrl+shift+e", () => {
    //     console.log('on add key pressed')
    //     if (allowShortcuts.includes(shortcutType.addRow)) {
    //         onAddRow?.(ctx.realRowIndex - 1);
    //     }
    // }, [ctx.isFocused, onAddRow, onDeleteRow, allowShortcuts]);

    // useHotkeys("ctrl+-", () => {
    //     if (allowShortcuts.includes(shortcutType.deleteRow)) {
    //         onDeleteRow?.(ctx.realRowIndex - 1);
    //     }
    // }, [ctx.isFocused, onAddRow, onDeleteRow, allowShortcuts]);


    useEffect(() => {
        if (!ctx.isFocused) return; // Ensure shortcut only works when cell is focused

        const handleKeyDown = (e: KeyboardEvent) => {
            console.log('key pressed', e)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === "+" || e.key === "=")) {
                e.preventDefault();
                if (allowShortcuts.includes(shortcutType.addRow)) {
                    console.log('ctx.realRowIndex', ctx.realRowIndex)
                    onAddRow?.(ctx.realRowIndex - 1);
                }
            } else if ((e.ctrlKey || e.metaKey) && e.key === "-") {
                e.preventDefault();
                if (allowShortcuts.includes(shortcutType.deleteRow)) {
                    onDeleteRow?.(ctx.realRowIndex - 1);
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [ctx.isFocused, onAddRow, onDeleteRow, allowShortcuts]);




    useEffect(() => {
        setCurrentValue(initialValueStr);
    }, [initialValue]);

    const numberKeys = "0123456789";
    const numberSeparators = [".", ","];

    // Format number according to Indian number system
    const formatIndianNumber = (value: string): string => {
        if (!format) return value;

        // Remove all commas first to get clean number
        const cleanValue = value.replace(/,/g, "");

        // Split into integer and decimal parts
        const parts = cleanValue.split(".");
        const integerPart = parts[0];
        const decimalPart = parts[1];

        if (!integerPart) return value;

        // Apply Indian number formatting (lakhs/crores)
        let formatted = integerPart;
        if (integerPart.length > 3) {
            const lastThree = integerPart.slice(-3);
            const remaining = integerPart.slice(0, -3);
            // Add commas every 2 digits from right to left for remaining part
            formatted = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
        }

        // Add decimal part back if exists
        return decimalPart !== undefined ? formatted + "." + decimalPart : formatted;
    };

    return (
        <CellWrapper
            onTouchEnd={handleDoubleTouch}
            onStringValueRequested={() => initialValueStr}
            onStringValueReceived={(v) => {
                const numValue = v === "" ? NaN : Number(v);

                onValueChanged?.(numValue);
            }}
            style={style}
            onDoubleClick={() => {
                if (ctx.isFocused) {
                    setCurrentValue(initialValue !== 0 && initialValueStr ? initialValueStr : "");
                    setEditMode(true);
                }
            }}
            onKeyDown={(e) => {
                if ((!isEditMode && numberKeys.includes(e.key)) || (allowSeparators && numberSeparators.includes(e.key))) {
                    setCurrentValue("");
                    setEditMode(true);
                } else if (!isEditMode && !ctx.isSelected && (e.key === "Enter" || e.key === "F2")) {
                    e.stopPropagation();
                    setCurrentValue(initialValue !== 0 && initialValueStr ? initialValueStr : "");
                    setEditMode(true);
                } else if (!isEditMode && ctx.isFocused && e.key == 'Enter') {
                    e.stopPropagation();
                    setCurrentValue(initialValue !== 0 && initialValueStr ? initialValueStr : "");
                    setEditMode(true);
                }
            }}
        >
            {isEditMode ? (
                <input
                    className="rg-input"
                    value={formatIndianNumber(currentValue)}
                    onChange={(e) => {
                        let newValue = e.currentTarget.value.replace(allowSeparators ? /[^0-9,.]/g : /[^0-9]/g, "");
                        if (numberSeparators.includes(newValue)) {
                            newValue = "0" + newValue;
                        }

                        // console.log('newValue', newValue);
                        const finalValue = newValue.replace(/,/g, "");
                        console.log('finalValue', finalValue);
                        // Store raw value without commas
                        setCurrentValue(finalValue);
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    onBlur={(e) => {
                        if (!escapePressedRef.current) {
                            // Use currentValue instead of e.currentTarget.value to avoid parsing formatted value
                            let value = currentValue.replace(/,/g, ".");
                            value.trim() === "" ? onValueChanged?.("" as any) : onValueChanged?.(Number(value));
                        }
                        setEditMode(false);
                        if (escapePressedRef.current) {
                            escapePressedRef.current = false;
                        }
                    }}
                    onCut={(e) => e.stopPropagation()}
                    onCopy={(e) => e.stopPropagation()}
                    onPaste={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                        const controlKeys = ["Escape", "Enter", "Tab"];
                        if (!controlKeys.includes(e.key)) {
                            e.stopPropagation();
                        }
                        if (e.key === "Escape") {
                            escapePressedRef.current = true;
                            setEditMode(false);
                        } else if (e.key === "Enter") {
                            // Use currentValue instead of e.currentTarget.value to avoid parsing formatted value
                            let value = currentValue.replace(/,/g, ".");
                            value.trim() === "" ? onValueChanged?.("" as any) : onValueChanged?.(Number(value));
                            setEditMode(false);
                        } else if (e.key === "Tab") {
                            // Use currentValue instead of e.currentTarget.value to avoid parsing formatted value
                            let value = currentValue.replace(/,/g, ".");
                            value.trim() === "" ? onValueChanged?.("" as any) : onValueChanged?.(Number(value));
                            setEditMode(false);
                        }
                    }}
                    autoFocus
                    ref={targetInputRef}
                />
            ) : (
                getFormattedValue()
            )}
        </CellWrapper>
    );
};